import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Box,
  Stack,
  ButtonBase,
  Avatar
} from '@mui/material';
import { PurchaseOrder, PurchaseOrderItemStatus, PurchaseOrderItemStatusColors, PurchaseOrderItem } from '@domain/purchasing';
import { POItemGroups } from '@domain/purchasing/models/PurchaseOrderItemGroups';
import { format, isValid } from 'date-fns';
import { Paper } from '@shared/ui/common/paper';
import { APP_CONFIG } from '@config/app.config';
import { useGenericDrawer } from '@shared/shell/drawer/useGenericDrawer';
import PurchaseOrderDetails from '../../details/PurchaseOrderDetails';
import { Label } from '@shared/ui/common';
import { usePurchaseOrderItems } from '../../../hooks';
import { ChevronRight } from '@mui/icons-material';

interface PurchaseOrdersTableProps {
  groupedItems?: POItemGroups[];
  ungroupedItems?: PurchaseOrderItem[];
}

export const PurchaseOrdersTable: React.FC<PurchaseOrdersTableProps> = ({
  groupedItems,
  ungroupedItems
}) => {
  const { openDrawer } = useGenericDrawer();
  const { updatePurchaseOrderItemStatus } = usePurchaseOrderItems();

  const handleStatusChange = async (itemId: string, newStatus: PurchaseOrderItemStatus) => {
    await updatePurchaseOrderItemStatus({ itemId, status: newStatus });
  };

  const handleRowClick = (item: PurchaseOrderItem) => {
    if (item.id) {
      openDrawer(
        `Purchase Order ${item.purchaseOrder?.poNumber || 'Details'}`,
        <PurchaseOrderDetails purchaseOrderId={item.purchaseOrder?.id} />
      );
    }
  };

  // Generate table data from either grouped items or regular purchase orders
  const tableData = React.useMemo(() => {
    const data: any[] = [];
    
    if (groupedItems && groupedItems.length > 0) {
      // Handle grouped items
      groupedItems.forEach(group => {
        data.push({
          groupName: group.name,
          groupId: group.id,
          items: group.items.map(item => ({
            item,
            order: item.purchaseOrder || {
              id: group.id,
              poNumber: group.name,
              items: [item]
            } as PurchaseOrder
          }))
        });
      });
    }
    
    // Add ungrouped items as a separate group
    if (ungroupedItems && ungroupedItems.length > 0) {
      data.push({
        groupName: 'Ungrouped Items',
        groupId: 'ungrouped',
        items: ungroupedItems.map(item => ({
          item,
          order: item.purchaseOrder || {
            id: 'ungrouped',
            poNumber: 'Ungrouped',
            items: [item]
          } as PurchaseOrder
        }))
      });
    }

    return data;
  }, [groupedItems, ungroupedItems]);

  // Don't render if no data
  if (!tableData || tableData.length === 0) {
    return (
      <Paper sx={{ p: 2, minHeight: '200px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No purchase order data available
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Products</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Quantity</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Expected Delivery</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData.map((groupData) => {
              const hasMultipleItems = groupData.items.length > 1;

              return (
                <React.Fragment key={groupData.groupId}>
                  {hasMultipleItems && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                          {groupData.groupName} with {groupData.items.length} item{groupData.items.length > 1 ? 's' : ''}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  {groupData.items.map(({ item, order }: { item: PurchaseOrderItem, order: PurchaseOrder }) => (
                    <TableRow
                      key={order.id + item.product.id}
                      hover
                      onClick={() => handleRowClick(item)}
                      sx={{
                        cursor: 'pointer',
                      }}
                    >
                      <TableCell
                        sx={{
                          border: 'none',
                          borderLeft: hasMultipleItems ? '4px solid' : 'none',
                          borderColor: APP_CONFIG.ui.theme.colors.secondary,
                        }}
                      >
                        <Stack spacing={1} direction="row" alignItems="center" gap={2}>
                          <Avatar
                            src={item.product.image}
                            variant="rounded"
                            sx={{ width: 40, height: 40, border: '1px solid #e0e0e0' }}
                          />
                          <Typography variant="body2">
                            {item.product.title}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="medium">
                          {item.quantity || 0}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Label
                          options={
                            Object.values(PurchaseOrderItemStatus).map(status => ({
                              value: status,
                              label: status,
                              color: PurchaseOrderItemStatusColors[status]
                            }))
                          }
                          currentValue={item.status}
                          onOptionChanged={(newStatus) => handleStatusChange(item.id!, newStatus as PurchaseOrderItemStatus)} />
                      </TableCell>

                      <TableCell>
                        {item.shipment?.processedDate && isValid(item.shipment.processedDate) ? (
                          <Typography variant="body2">
                            {format(item.shipment.processedDate, 'EEEE, MMM dd')}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No shipment for item
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <ChevronRight />
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}; 